import { Disclaimer, Show, Spacer } from 'components/atoms';
import { ContentRow, DirektkaufDialog, DirektkaufTokenBanner, Head } from 'components/molecules';
import { Layout, PlusRow, PlusTeaser, ProfileHeader } from 'components/profiles';
import { Dokumente, Insolvenz, OrganeUndNetzwerk, Stammdaten, ÄhnlicheUnternehmen } from 'content/unternehmen';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { ReferenzType } from 'types';
import { UnternehmenFreeDto } from 'types/unternehmen';
import { getCanonicalUrl } from 'utils';
import { getUnternehmenMetaTagContent } from 'utils/functions/seo';
import { useDirektkaufTokenMemory, useTranslation } from 'utils/hooks';
import { useCheckForGisaUpdatesMutation } from 'utils/hooks/mutations';
import { getUnternehmenStaticProps } from 'utils/profileStaticProps';

interface UnternehmensProfilPageProps
{
  unternehmen: UnternehmenFreeDto;
}

const UnternehmensProfilPage: NextPage<UnternehmensProfilPageProps> = (props) => 
{
  const { unternehmen } = props;
  const { t } = useTranslation('profiles');
  const router = useRouter();

  const referral = parseInt(router.query.referral?.toString() ?? '-1');
  const meta = getUnternehmenMetaTagContent(t, unternehmen);

  useDirektkaufTokenMemory();

  const gisaMutation = useCheckForGisaUpdatesMutation();
  useEffect(() => gisaMutation.mutate({ type: 'unternehmen', id: unternehmen.fbNr }), []);

  const type = unternehmen.auslandsInfo !== null ? ReferenzType.AusländischesUnternehmen : ReferenzType.Unternehmen;

  return (
    <>
    <Layout key={unternehmen.fbNr}>

      <Head
        title={meta.title}
        description={meta.description}
      >
        <meta name="keywords" content={meta.keywords} />
        <link rel="canonical" href={`${getCanonicalUrl()}/u/${unternehmen.fbNr}`} />
        {!unternehmen.isIndexed && <meta name="robots" content="noindex" />}
      </Head>

      <ProfileHeader
        title={<>{unternehmen.name} <span style={{ display: 'inline-block', margin:0 }}>{`(${t('formats:fbNr', { value: unternehmen.fbNr })})`}</span></>}
        subTitle={referral >= 0 ? t('profiles:previousName', { name: unternehmen.wortlaute[referral].item.fullName }) : undefined}
        id={unternehmen.fbNr}
        type={type}
        tabs={[
          {
            text: t('profiles:navigation.allgemeines'),
            href: 'allgemeines',
          },
          {
            text: t('profiles:navigation.leitungsorgane'),
            href: 'personen',
            hidden: !unternehmen.leitungsorgane,
          },
          {
            text: t('profiles:navigation.netzwerk'),
            href: 'netzwerk',
            hidden: Object.keys(unternehmen.netzwerk).length === 0,
          },
          {
            text: t('profiles:navigation.aufsichtsrat'),
            href: 'aufsichtsrat',
            hidden: !unternehmen.kontrollorgane,
          },
          {
            text: t('profiles:navigation.prokura'),
            href: 'prokura',
            hidden: !unternehmen.prokuraorgane,
          },
          {
            text: t('profiles:navigation.insolvenz'),
            href: 'insolvenz',
            hidden: unternehmen.insolvenzen.length === 0,
          },
          {
            text: t('profiles:navigation.dokumente'),
            href: 'dokumente',
          },
          {
            text: t('profiles:navigation.plusInhalte'),
            href: 'plus-inhalte',
          },
        ]}
      />

      <DirektkaufTokenBanner />

      <Show when={unternehmen.insolvenzen.length !== 0}>
        <Disclaimer text={t('profiles:unternehmen.insolvencyDisclaimer')} href="#insolvenz" />
        <Spacer height={2} />
      </Show>
      <div style={{ position:'relative' }}>
        <dl>
          <Stammdaten unternehmen={unternehmen} />

          <OrganeUndNetzwerk unternehmen={unternehmen} />
          <ContentRow id="rechtstatsachen" title={t('profiles:unternehmen.rechtstatsachen.label')} groupItem>
            <PlusRow />
          </ContentRow>

          <Insolvenz unternehmen={unternehmen} />

          <Dokumente unternehmen={unternehmen} />
        </dl>
        
        <PlusTeaser
          plusInhalte={unternehmen.premiumInfo}
        />

        <dl>
          <ÄhnlicheUnternehmen unternehmen={unternehmen} />
        </dl>
      </div>
      <DirektkaufDialog id={unternehmen.fbNr} type={type} />
    </Layout>
    </>
  );
};

export default UnternehmensProfilPage;

export const getStaticPaths: GetStaticPaths = async () =>
{
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async (ctx) =>
{
  const props = await getUnternehmenStaticProps(ctx, false);
  return props;
};
